import { AppConfirmDialog } from "components/common/dialogs";
import { isVatIncluded, getPaymentVat, amountAfterVat } from "../../utils/contractVat";
import { refreshAfterSave } from "utils/workflowOperations";
import React, { useState, useEffect, useMemo } from "react";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  Chip,
  Tooltip,
  Alert,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import DataTable from "components/common/tables/DataTable";
import ContractPaymentProfileDetails from "./ContractPaymentProfileDetails";
import PaymentProfileModal from "./PaymentProfileModal";
import { getToken } from "services/auth.service";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import { useAuthorization } from "auth/AuthorizationContext";
import { formatAmount, formatCurrencyWithUnit, formatDate } from "utils/formatters";
import { AppButton } from "components/common/buttons";
import { indexPaymentItems, filterPaymentsByItems, getContractPaymentAmount } from "./paymentScopes";

const PaymentProfileTable = ({
  contractId,
  contractNumber,
  contractName,
  contractItems = [],
  contractData = null, // Thêm contractData để lấy VAT rate
  refreshKey = 0, // Thêm refreshKey prop
  scopeItems = contractItems,
  scopeIds = null,
  sections = [],
  readOnly = false,
}) => {
  const { can } = useAuthorization();
  const canCreate = can(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_CREATE);
  const canUpdate = can(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_UPDATE);
  const canDelete = can(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_DELETE);
  const included = isVatIncluded(contractData);
  const [paymentProfiles, setPaymentProfiles] = useState([]);
  const [contractPayments, setContractPayments] = useState([]); // Tất cả thanh toán của hợp đồng
  const itemIndex = useMemo(() => indexPaymentItems(contractItems), [contractItems]);
  const visiblePayments = filterPaymentsByItems(contractPayments, itemIndex, scopeIds);
  // Empty periods belong to the contract until their first item payment is added.
  const visibleProfiles = scopeIds == null ? paymentProfiles : paymentProfiles.filter((profile) =>
    visiblePayments.some((payment) => payment.paymentProfileId === profile.paymentProfileId)
    || !contractPayments.some((payment) => payment.paymentProfileId === profile.paymentProfileId));
  const [profilesLoading, setLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [paymentsError, setPaymentsError] = useState("");
  const [paymentRefreshWarning, setPaymentRefreshWarning] = useState("");
  const loading = profilesLoading || paymentsLoading;
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Permission Snackbar state
  const [permissionSnackbar, setPermissionSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  const loadPaymentProfiles = async () => {
    setLoading(true);
    setProfileError("");
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/PaymentProfile/contract/${contractId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success === false || result.Success === false) throw new Error("Không thể tải kỳ thanh toán.");
        setPaymentProfiles(result.data || []);
        return true;
      } else {
        throw new Error("Failed to load payment profiles");
      }
    } catch (error) {
      console.error("Error loading payment profiles:", error);
      showSnackbar("Lỗi tải danh sách kỳ thanh toán", "error");
      setProfileError("Không thể tải danh sách kỳ thanh toán. Vui lòng thử lại.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const loadContractPayments = async () => {
    if (!contractId) return;
    setPaymentsLoading(true);
    setPaymentsError("");
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/ContractPayment/contract/${contractId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success === false || result.Success === false) throw new Error("Không thể tải thanh toán.");
        setContractPayments(result.data || []);
        return true;
      } else {
        throw new Error("Không thể tải dữ liệu thanh toán.");
      }
    } catch (error) {
      showSnackbar("Không thể tải dữ liệu thanh toán.", "error");
      setPaymentsError("Không thể tải dữ liệu thanh toán theo scope. Vui lòng thử lại.");
      return false;
    } finally {
      setPaymentsLoading(false);
    }
  };

  useEffect(() => {
    if (contractId) {
      loadPaymentProfiles();
      loadContractPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, refreshKey]); // Thêm refreshKey vào dependency

  const refreshPaymentDetails = async () => {
    const results = await Promise.all([loadPaymentProfiles(), loadContractPayments()]);
    if (results.some((result) => result === false)) {
      setPaymentRefreshWarning("Dữ liệu đã lưu nhưng chưa tải lại được. Hãy thử tải lại; không gửi lại thao tác vừa lưu.");
      throw new Error("Không thể tải lại thanh toán.");
    }
    setPaymentRefreshWarning("");
  };

  // Tính thành tiền cho một payment
  const calculatePaymentAmount = (payment) => getContractPaymentAmount(payment, itemIndex.get(payment.contractItemId));

  // Tính tổng trước thuế cho một payment profile
  const getTotalBeforeTaxForProfile = (paymentProfileId) => {
    const profilePayments = visiblePayments.filter(
      (payment) => payment.paymentProfileId === paymentProfileId
    );

    return Math.round(
      profilePayments.reduce((total, payment) => {
        return total + calculatePaymentAmount(payment);
      }, 0)
    );
  };

  // Tính tổng sau thuế cho một payment profile
  const getTotalAfterTaxForProfile = (paymentProfile) => {
    const beforeTax = getTotalBeforeTaxForProfile(
      paymentProfile.paymentProfileId
    );
    return amountAfterVat(beforeTax, getPaymentVat(paymentProfile));
  };

  // Tính tổng giá trị hợp đồng trước thuế
  const getContractTotalBeforeTax = () => {

    // Thử cách 1: Tính tổng tất cả items có contractAmount > 0 (bất kể có children hay không)
    const total = Math.round(
      scopeItems.reduce((sum, item) => {
        if (item.contractAmount && item.contractAmount > 0) {
          return sum + item.contractAmount;
        }
        return sum;
      }, 0)
    );

    return total;
  }; // Tính tổng giá trị hợp đồng sau thuế (VAT của hợp đồng)
  const getContractTotalAfterTax = () => {
    const beforeTax = getContractTotalBeforeTax();
    // Sử dụng VAT từ contractData giống như ContractItemsTable
    const vatRate = (contractData?.vat ?? 0) / 100;
    const vatAmount = beforeTax * vatRate;
    return Math.round(beforeTax + vatAmount);
  };

  // Tính tổng đã thanh toán trước thuế (tất cả các kỳ)
  const getTotalPaidBeforeTax = () => {
    return Math.round(
      visiblePayments.reduce((total, payment) => {
        return total + calculatePaymentAmount(payment);
      }, 0)
    );
  };

  // Tính tổng đã thanh toán sau thuế (cộng tổng của từng kỳ đã tính VAT)
  const getTotalPaidAfterTax = () => {
    return Math.round(
      paymentProfiles.reduce((total, profile) => {
        return total + getTotalAfterTaxForProfile(profile);
      }, 0)
    );
  };

  // Handle add payment profile with permission check
  const handleAddPaymentProfile = async () => {
    try {
      const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_CREATE);
      if (result.hasPermission) {
        setEditingProfile(null);
        setShowModal(true);
      } else {
        setPermissionSnackbar({
          open: true,
          message: "Bạn không có quyền tạo kỳ thanh toán",
          severity: "warning",
        });
      }
    } catch (error) {
      console.error("Error checking permission:", error);
      setPermissionSnackbar({
        open: true,
        message: "Lỗi kiểm tra quyền truy cập",
        severity: "error",
      });
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSaveProfile = async (profileData, profileId = null) => {
    try {
      const token = getToken();
      const url = profileId
        ? `${process.env.REACT_APP_API_URL}/api/PaymentProfile/${profileId}`
        : `${process.env.REACT_APP_API_URL}/api/PaymentProfile`;

      const method = profileId ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        const saved = await response.json();
        if (saved.success === false || saved.Success === false) throw new Error(saved.message || saved.Message || "Không thể lưu dữ liệu.");
        const refreshed = await refreshAfterSave(async () => {
          if (await loadPaymentProfiles() === false) throw new Error("refresh failed");
        }, showSnackbar);
        if (refreshed) showSnackbar(
          profileId
            ? "Cập nhật kỳ thanh toán thành công"
            : "Thêm kỳ thanh toán thành công"
        );
      } else {
        const error = await response.json();
        throw new Error(error.message || "Lỗi khi lưu kỳ thanh toán");
      }
    } catch (error) {
      console.error("Error saving payment profile:", error);
      showSnackbar(error.message || "Lỗi khi lưu kỳ thanh toán", "error");
      throw error;
    }
  };

  const handleDeleteProfile = async (profileId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/PaymentProfile/${profileId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const refreshed = await refreshAfterSave(async () => {
          const results = await Promise.all([loadPaymentProfiles(), loadContractPayments()]);
          if (results.some((result) => result === false)) throw new Error("refresh failed");
        }, showSnackbar);
        if (refreshed) showSnackbar("Xóa kỳ thanh toán thành công");
      } else {
        const error = await response.json();
        throw new Error(error.message || "Lỗi khi xóa kỳ thanh toán");
      }
    } catch (error) {
      console.error("Error deleting payment profile:", error);
      showSnackbar(error.message || "Lỗi khi xóa kỳ thanh toán", "error");
    }
  };

  // Hàm mở dialog xác nhận xóa
  const handleDeleteClick = (profile) => {
    setProfileToDelete(profile);
    setDeleteDialogOpen(true);
  };

  // Hàm xác nhận xóa
  const handleConfirmDelete = async () => {
    if (deleting) return;
    if (profileToDelete) {
      setDeleting(true);
      try {
        await handleDeleteProfile(profileToDelete.paymentProfileId);
        setDeleteDialogOpen(false);
        setProfileToDelete(null);
      } finally {
        setDeleting(false);
      }
    }
  };

  // Hàm hủy xóa
  const handleCancelDelete = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setProfileToDelete(null);
  };

  const handleEdit = async (profile) => {
    if (readOnly || loading || !canUpdate) return;
    try {
      const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_UPDATE);
      if (result.hasPermission) {
        setEditingProfile(profile);
        setShowModal(true);
      } else {
        setPermissionSnackbar({
          open: true,
          message: "Bạn không có quyền chỉnh sửa kỳ thanh toán",
          severity: "warning",
        });
      }
    } catch (error) {
      console.error("Error checking permission:", error);
      setPermissionSnackbar({
        open: true,
        message: "Lỗi kiểm tra quyền truy cập",
        severity: "error",
      });
    }
  };

  const handleDelete = async (profile) => {
    if (readOnly || loading || !canDelete) return;
    try {
      const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_DELETE);
      if (result.hasPermission) {
        handleDeleteClick(profile);
      } else {
        setPermissionSnackbar({
          open: true,
          message: "Bạn không có quyền xóa kỳ thanh toán",
          severity: "warning",
        });
      }
    } catch (error) {
      console.error("Error checking permission:", error);
      setPermissionSnackbar({
        open: true,
        message: "Lỗi kiểm tra quyền truy cập",
        severity: "error",
      });
    }
  };

  const columns = [
    {
      accessorKey: "paymentName", header: "Tên kỳ thanh toán", size: 120,
    },
    {
      accessorKey: "paymentDate",
      header: "Ngày thanh toán",
      size: 90,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      accessorKey: "vat",
      header: "VAT",
      size: 50,
      Cell: ({ cell }) => (
        <Chip
          label={`${cell.getValue()}%`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            border: "none",
            backgroundColor: "#e3f2fd",
            color: "#1976d2",
          }}
        />
      ),
    },
    {
      id: "totalBeforeTax",
      header: "Tổng trước thuế",
      size: 100,
      Cell: ({ row }) => {
        const total = getTotalBeforeTaxForProfile(
          row.original.paymentProfileId
        );
        return (
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
              {formatAmount(total)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              VNĐ
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "totalAfterTax",
      header: "Tổng sau thuế",
      size: 100,
      Cell: ({ row }) => {
        const total = getTotalAfterTaxForProfile(row.original);
        return (
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontWeight: 600, color: "#1976d2", fontSize: "12px", }}
            >
              {formatAmount(total)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              VNĐ
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "notes",
      header: "Ghi chú",
      size: 180,
      Cell: ({ cell }) => {
        const notes = cell.getValue();
        return (
          <Typography variant="body2" color="text.secondary">
            {notes || "—"}
          </Typography>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Ngày tạo",
      size: 90,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
  ];

  if (profileError || paymentsError) return <Alert severity="error"
    action={<AppButton onClick={() => { loadPaymentProfiles(); loadContractPayments(); }}>Thử lại</AppButton>}>
    {paymentRefreshWarning || profileError || paymentsError}
  </Alert>;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
          >
            Kỳ thanh toán
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: "Inter, sans-serif",
              color: "text.secondary",
              fontStyle: "italic",
            }}
          >
            Bấm nút thu/mở để xem chi tiết theo loại hạng mục và tỉnh/xã.
          </Typography>
          {scopeIds != null && <Typography variant="body2" color="text.secondary">
            Số tiền được lọc theo scope. Kỳ thanh toán dùng chung cho hợp đồng; chỉnh sửa hoặc xóa kỳ áp dụng cho mọi scope.
          </Typography>}
        </Box>
        {canCreate && <AppButton
          startIcon={<AddIcon />}
          variant="contained"
          onClick={handleAddPaymentProfile}
          disabled={readOnly}
        >
          Thêm kỳ thanh toán
        </AppButton>}
      </Box>

      <DataTable
        columns={columns}
        data={visibleProfiles}
        state={{ isLoading: loading }}
        enableRowActions={canUpdate || canDelete}
        renderRowActions={({ row }) => <Box sx={{ display: "flex", justifyContent: "center" }}>
          {canUpdate && <Tooltip title="Chỉnh sửa kỳ thanh toán"><span>
            <IconButton size="small" disabled={readOnly || loading}
              aria-label={`Chỉnh sửa kỳ thanh toán ${row.original.paymentName}`}
              onClick={(event) => { event.stopPropagation(); handleEdit(row.original); }}>
              <EditIcon fontSize="small" color="primary" />
            </IconButton>
          </span></Tooltip>}
          {canDelete && <Tooltip title="Xóa kỳ thanh toán"><span>
            <IconButton size="small" color="error" disabled={readOnly || loading}
              aria-label={`Xóa kỳ thanh toán ${row.original.paymentName}`}
              onClick={(event) => { event.stopPropagation(); handleDelete(row.original); }}>
              <DeleteIcon fontSize="small" color="error" />
            </IconButton>
          </span></Tooltip>}
        </Box>}
        getRowId={(row) => String(row.paymentProfileId)}
        enableExpanding
        onRowClick={(_, row) => row.toggleExpanded()}
        muiExpandButtonProps={({ row }) => ({ "aria-label": `${row.getIsExpanded() ? "Thu" : "Mở"} kỳ thanh toán ${row.original.paymentName}` })}
        renderDetailPanel={({ row }) => <ContractPaymentProfileDetails
          profile={{ ...row.original, vat: getPaymentVat(row.original) }}
          payments={contractPayments.filter((payment) => payment.paymentProfileId === row.original.paymentProfileId)}
          allPayments={contractPayments} contractItems={contractItems} sections={sections}
          loading={loading} onChanged={refreshPaymentDetails} />}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableDensityToggle={false}
        enableColumnActions={false}
        enablePagination={false}
        enableBottomToolbar={false}
        maxTableHeight="none"
      />

      {/* Thông tin tóm tắt hợp đồng và thanh toán */}
      {!loading && <Paper
        sx={{
          mt: 3,
          p: 3,
          border: "1px solid #e0e0e0",
          boxShadow: "none",
          elevation: 0,
          backgroundColor: "#f8f9fa",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            mb: 2,
            color: "#333",
          }}
        >
          {scopeIds == null ? "Tóm tắt tài chính" : "Tóm tắt tài chính theo scope đang chọn"}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
          }}
        >
          {/* Thông tin hợp đồng */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "#1976d2",
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
              }}
            >
              Giá trị hợp đồng
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {!included && (<Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Trước thuế:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrencyWithUnit(getContractTotalBeforeTax())}
                </Typography>
              </Box>)}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  {included ? "Sau thuế (đã bao gồm VAT):" : `Sau thuế (${contractData?.vat ?? 0}% VAT):`}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#1976d2" }}
                >
                  {formatCurrencyWithUnit(getContractTotalAfterTax())}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Thông tin đã thanh toán */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "#4caf50",
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
              }}
            >
              Đã thanh toán
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Trước thuế:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrencyWithUnit(getTotalPaidBeforeTax())}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Sau thuế:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#4caf50" }}
                >
                  {formatCurrencyWithUnit(getTotalPaidAfterTax())}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Thông tin còn lại */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "#ff9800",
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
              }}
            >
              Còn lại
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {!included && (<Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Trước thuế:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrencyWithUnit(
                    getContractTotalBeforeTax() - getTotalPaidBeforeTax()
                  )}
                </Typography>
              </Box>)}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Sau thuế:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#ff9800" }}
                >
                  {formatCurrencyWithUnit(
                    getContractTotalAfterTax() - getTotalPaidAfterTax()
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Thanh tiến độ thanh toán */}
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Tiến độ thanh toán
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {(() => {
                const totalAfterTax = getContractTotalAfterTax();
                const paidAfterTax = getTotalPaidAfterTax();

                if (totalAfterTax === 0) {
                  return "0.0%";
                }

                const percentage = (paidAfterTax / totalAfterTax) * 100;
                return isFinite(percentage)
                  ? percentage.toFixed(1) + "%"
                  : "0.0%";
              })()}
            </Typography>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: 8,
              backgroundColor: "#e0e0e0",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${(() => {
                  const totalAfterTax = getContractTotalAfterTax();
                  const paidAfterTax = getTotalPaidAfterTax();

                  if (totalAfterTax === 0) {
                    return 0;
                  }

                  const percentage = (paidAfterTax / totalAfterTax) * 100;
                  return Math.min(isFinite(percentage) ? percentage : 0, 100);
                })()}%`,
                height: "100%",
                backgroundColor: "#4caf50",
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </Box>
      </Paper>}

      {/* Payment Profile Modal */}
      <PaymentProfileModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingProfile(null);
        }}
        onSave={handleSaveProfile}
        contractId={contractId}
        contractNumber={contractNumber}
        contractName={contractName}
        editingProfile={editingProfile}
      />

      {/* Delete Confirmation Dialog */}
      <AppConfirmDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        title="Xác nhận xóa kỳ thanh toán"
        loading={deleting}
        type="delete"
        onConfirm={handleConfirmDelete}
      >
        <Typography sx={{ color: "#666", fontSize: "16px", lineHeight: 1.5 }}>
          Bạn có chắc chắn muốn xóa kỳ thanh toán "
          <b>{profileToDelete?.paymentName}</b>" không?
          <Divider sx={{ my: 2, mx: 5 }} />
        </Typography>
        <Typography sx={{ color: "#666", fontSize: "13px", lineHeight: 1.5, mt: 1 }}>
          <i>Toàn bộ thanh toán hạng mục của kỳ này ở mọi scope sẽ bị xóa.</i>
          <br />
          <i><b>Hành động này không thể hoàn tác.</b></i>
        </Typography>
      </AppConfirmDialog>

      {/* Snackbar */}
      <AppSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        severity={snackbar.severity}
        message={snackbar.message}
      />

      {/* Permission Snackbar */}
      <AppSnackbar
        open={permissionSnackbar.open}
        onClose={() =>
          setPermissionSnackbar({ ...permissionSnackbar, open: false })
        }
        severity={permissionSnackbar.severity}
        message={permissionSnackbar.message}
      />
    </Box>
  );
};

export default PaymentProfileTable;
